// ============================================
// TanStack Query Configuration
// ============================================

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { DefaultOptions } from '@tanstack/react-query';
import { isApiError } from '@conai/api-client';
import toast from 'react-hot-toast';

// Parse Django ErrorDetail format into readable lines
// Handles: {'field': [ErrorDetail(string='...', code='...')], 'field2': [...]}
// Also handles multiple ErrorDetails per field
function parseBackendError(raw: string): string[] {
  const lines: string[] = [];

  // Match: 'field': [ErrorDetail(string='msg', code='...'), ...]
  const fieldRegex = /'([^']+)':\s*\[([^\]]+)\]/g;
  for (const fieldMatch of raw.matchAll(fieldRegex)) {
    const field = fieldMatch[1];
    const detailsStr = fieldMatch[2];
    const msgRegex = /ErrorDetail\(string='([^']+)'/g;
    for (const msgMatch of detailsStr.matchAll(msgRegex)) {
      lines.push(`${field}: ${msgMatch[1]}`);
    }
  }

  if (lines.length > 0) return lines;

  // Fallback: any ErrorDetail string
  const detailRegex = /ErrorDetail\(string='([^']+)'/g;
  for (const m of raw.matchAll(detailRegex)) {
    lines.push(m[1]);
  }

  return lines.length > 0 ? lines : [raw];
}

// Default options for all queries
const defaultOptions: DefaultOptions = {
  queries: {
    // Stale time: data considered fresh for 1 minute
    staleTime: 1 * 60 * 1000,
    // Cache time: keep unused data in cache for 5 minutes
    gcTime: 5 * 60 * 1000,
    // Retry failed requests up to 3 times
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (isApiError(error) && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 3;
    },
    // Refetch on window focus in production
    refetchOnWindowFocus: import.meta.env.PROD,
    // Avoid duplicate refetches when components remount in development
    refetchOnMount: false,
  },
  mutations: {
    // Retry mutations once
    retry: 1,
  },
};

// Create query client instance
export const queryClient = new QueryClient({
  defaultOptions,
});

// Query keys factory for consistent cache key management
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  // Stores
  stores: {
    all: ['stores'] as const,
    lists: () => [...queryKeys.stores.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.stores.lists(), filters] as const,
    details: () => [...queryKeys.stores.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.stores.details(), id] as const,
  },

  // Categories
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.categories.lists(), filters] as const,
    tree: () => [...queryKeys.categories.all, 'tree'] as const,
    stats: () => [...queryKeys.categories.all, 'stats'] as const,
    details: () => [...queryKeys.categories.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.categories.details(), id] as const,
  },

  // Products
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
    byBarcode: (barcode: string) => [...queryKeys.products.all, 'barcode', barcode] as const,
  },

  // Inventory
  inventory: {
    all: ['inventory'] as const,
    lists: () => [...queryKeys.inventory.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.inventory.lists(), filters] as const,
    details: () => [...queryKeys.inventory.all, 'detail'] as const,
    detail: (productId: string, storeId: string) =>
      [...queryKeys.inventory.details(), productId, storeId] as const,
    stats: (storeId?: string) => [...queryKeys.inventory.all, 'stats', storeId] as const,
    lowStock: (storeId?: string) => [...queryKeys.inventory.all, 'low-stock', storeId] as const,
    outOfStock: (storeId?: string) => [...queryKeys.inventory.all, 'out-of-stock', storeId] as const,
    adjustments: (filters: object) =>
      [...queryKeys.inventory.all, 'adjustments', filters] as const,
    drift: (params: object) => [...queryKeys.inventory.all, 'drift', params] as const,
    // Lô & kiểm kê nằm DƯỚI cùng gốc ['inventory'] là cố ý: chốt phiên kiểm kê
    // và ghi lô kèm apply_to_stock đều làm đổi tồn kho, nên một lần
    // invalidate `inventory.all` phải quét sạch cả bảng tồn lẫn hai nhánh này.
    batches: (params: object) => [...queryKeys.inventory.all, 'batches', params] as const,
    expiringBatches: (params: object) =>
      [...queryKeys.inventory.all, 'batches-expiring', params] as const,
    stocktakes: (params: object) => [...queryKeys.inventory.all, 'stocktakes', params] as const,
    stocktakeDetail: (id: string) =>
      [...queryKeys.inventory.all, 'stocktakes', 'detail', id] as const,
  },

  // Stock Imports
  stockImports: {
    all: ['stock-imports'] as const,
    lists: () => [...queryKeys.stockImports.all, 'list'] as const,
    list: (filters: object) =>
      [...queryKeys.stockImports.lists(), filters] as const,
    details: () => [...queryKeys.stockImports.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.stockImports.details(), id] as const,
  },

  // Orders
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
    byCustomer: (customerId: string) =>
      [...queryKeys.orders.all, 'customer', customerId] as const,
  },

  // Payments
  payments: {
    all: ['payments'] as const,
    lists: () => [...queryKeys.payments.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.payments.lists(), filters] as const,
    details: () => [...queryKeys.payments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.payments.details(), id] as const,
    byOrder: (orderId: string) => [...queryKeys.payments.all, 'order', orderId] as const,
  },

  // Customers
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.customers.lists(), filters] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customers.details(), id] as const,
    byPhone: (phone: string) => [...queryKeys.customers.all, 'phone', phone] as const,
    // Duyệt xác minh 18+
    ageVerifications: () => [...queryKeys.customers.all, 'age-verifications'] as const,
    ageVerificationList: (filters: object) =>
      [...queryKeys.customers.ageVerifications(), 'list', filters] as const,
    ageVerificationDetail: (customerId: string) =>
      [...queryKeys.customers.ageVerifications(), 'detail', customerId] as const,
  },

  // Loyalty
  loyalty: {
    all: ['loyalty'] as const,
    lists: () => [...queryKeys.loyalty.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.loyalty.lists(), filters] as const,
    details: () => [...queryKeys.loyalty.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.loyalty.details(), id] as const,
    balance: (customerId: string) => [...queryKeys.loyalty.all, 'balance', customerId] as const,
    history: (customerId: string, filters: object) =>
      [...queryKeys.loyalty.all, 'history', customerId, filters] as const,
    tiers: () => [...queryKeys.loyalty.all, 'tiers'] as const,
    /**
     * Ngưỡng hạng theo currency (`/loyalty/tier-config/`, mục #45). Nằm dưới
     * cùng gốc `['loyalty']` để `recalculate-tiers` invalidate được cả cấu hình
     * lẫn danh sách tài khoản chỉ bằng `queryKeys.loyalty.all`.
     */
    tierConfig: () => [...queryKeys.loyalty.all, 'tier-config'] as const,
  },

  // Sổ bằng chứng bán hàng 18+ (GET /audit/restricted-sales/)
  restrictedSales: {
    all: ['restricted-sales'] as const,
    lists: () => [...queryKeys.restrictedSales.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.restrictedSales.lists(), filters] as const,
  },

  // Access Control
  accessControl: {
    all: ['access-control'] as const,
    tokens: () => [...queryKeys.accessControl.all, 'tokens'] as const,
    tokenList: (filters: object) => [...queryKeys.accessControl.tokens(), filters] as const,
    events: () => [...queryKeys.accessControl.all, 'events'] as const,
    eventList: (filters: object) => [...queryKeys.accessControl.events(), filters] as const,
    sessions: () => [...queryKeys.accessControl.all, 'sessions'] as const,
    sessionList: (filters: object) => [...queryKeys.accessControl.sessions(), filters] as const,
    sessionDetail: (id: string) => [...queryKeys.accessControl.sessions(), id] as const,
  },

  // Nedap AtWork (door access control)
  atWork: {
    all: ['atwork'] as const,
    doors: () => [...queryKeys.atWork.all, 'doors'] as const,
    events: (doorId: string, filters: object) =>
      [...queryKeys.atWork.all, 'events', doorId, filters] as const,
    dailyStats: (filters: object) =>
      [...queryKeys.atWork.all, 'daily-stats', filters] as const,
    recentDenied: (filters: object) =>
      [...queryKeys.atWork.all, 'recent-denied', filters] as const,
    commands: (filters: object) =>
      [...queryKeys.atWork.all, 'commands', filters] as const,
    health: () => [...queryKeys.atWork.all, 'health'] as const,
  },

  // Promotions
  promotions: {
    all: ['promotions'] as const,
    lists: () => [...queryKeys.promotions.all, 'list'] as const,
    list: (filters: object) =>
      [...queryKeys.promotions.lists(), filters] as const,
    details: () => [...queryKeys.promotions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.promotions.details(), id] as const,
    active: (storeId?: string) => [...queryKeys.promotions.all, 'active', storeId] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    sales: (params: object) => [...queryKeys.reports.all, 'sales', params] as const,
    dailyClose: (params: object) =>
      [...queryKeys.reports.all, 'daily-close', params] as const,
    inventory: (params: object) =>
      [...queryKeys.reports.all, 'inventory', params] as const,
    orders: (params: object) =>
      [...queryKeys.reports.all, 'orders', params] as const,
    customers: (params: object) =>
      [...queryKeys.reports.all, 'customers', params] as const,
    customerSummary: (params: object) =>
      [...queryKeys.reports.all, 'customer-summary', params] as const,
    revenueTrend: (params: object) =>
      [...queryKeys.reports.all, 'revenue-trend', params] as const,
    orderStatusTrend: (params: object) =>
      [...queryKeys.reports.all, 'order-status-trend', params] as const,
    topProductsByCategory: (params: object) =>
      [...queryKeys.reports.all, 'top-products-by-category', params] as const,
    dashboardTransactions: (params: object) =>
      [...queryKeys.reports.all, 'dashboard-transactions', params] as const,
    dashboardOverview: (params: object) =>
      [...queryKeys.reports.all, 'dashboard-overview', params] as const,
    dashboard: (storeId?: string) => [...queryKeys.reports.all, 'dashboard', storeId] as const,
  },

  // Suppliers
  suppliers: {
    all: ['suppliers'] as const,
    lists: () => [...queryKeys.suppliers.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.suppliers.lists(), filters] as const,
    details: () => [...queryKeys.suppliers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.suppliers.details(), id] as const,
    // Bug #186: các thẻ KPI đọc từ /suppliers/stats/, trước đây gắn key viết tay
    // ['suppliers','stats'] ngay trong hook. Nó là NHÁNH ANH EM của
    // ['suppliers','list'], nên invalidate danh sách không đụng tới nó — xoá một
    // nhà cung cấp thì bảng cập nhật còn thẻ "Total Supplier" giữ số cũ.
    stats: () => [...queryKeys.suppliers.all, 'stats'] as const,
  },

  // Góp ý của khách — hàng đợi xử lý phía cửa hàng (`/feedback/staff/`, bug #208)
  feedback: {
    all: ['feedback'] as const,
    lists: () => [...queryKeys.feedback.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.feedback.lists(), filters] as const,
    details: () => [...queryKeys.feedback.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.feedback.details(), id] as const,
    // Thẻ tổng hợp là NHÁNH RIÊNG cạnh `list`, và nó nhận CÙNG bộ filter với
    // danh sách. Vì thế nó phải nằm dưới cùng gốc `['feedback']` — đổi trạng
    // thái một góp ý làm sai CẢ bảng LẪN thẻ, invalidate thiếu một bên là lặp
    // lại đúng bug #186 của Suppliers (bảng cập nhật, thẻ KPI giữ số cũ).
    summaries: () => [...queryKeys.feedback.all, 'summary'] as const,
    summary: (filters: object) => [...queryKeys.feedback.summaries(), filters] as const,
  },

  // Settings
  settings: {
    all: ['settings'] as const,
    lists: () => [...queryKeys.settings.all, 'list'] as const,
    general: () => [...queryKeys.settings.all, 'general'] as const,
    notifications: () => [...queryKeys.settings.all, 'notifications'] as const,
    security: () => [...queryKeys.settings.all, 'security'] as const,
    integrations: () => [...queryKeys.settings.all, 'integrations'] as const,
    stats: () => [...queryKeys.settings.all, 'stats'] as const,
  },

  // Audit Logs
  auditLogs: {
    all: ['audit-logs'] as const,
    lists: () => [...queryKeys.auditLogs.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.auditLogs.lists(), filters] as const,
    details: () => [...queryKeys.auditLogs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.auditLogs.details(), id] as const,
    stats: () => [...queryKeys.auditLogs.all, 'stats'] as const,
  },

  // Khiếu nại hoá đơn sau bán (sheet 2 #58/#60)
  disputes: {
    all: ['disputes'] as const,
    lists: () => [...queryKeys.disputes.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.disputes.lists(), filters] as const,
  },

  // Đổi/trả hàng phía nhân viên (sheet 2 #55/#56/#59)
  returns: {
    all: ['returns'] as const,
    lists: () => [...queryKeys.returns.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.returns.lists(), filters] as const,
  },
} as const;

// Helper type for invalidating queries
export type QueryKeyFactory = typeof queryKeys;

// Export provider component for re-export
export { QueryClientProvider };
